import { supabase } from '../lib/supabaseClient';
import { CotisationSalarialeRow, CotisationSalariale } from '../types';

/**
 * @fileoverview Service for fetching and transforming cotisations_salariales data from Supabase
 * 
 * This service handles all interactions with the cotisations_salariales table in Supabase,
 * including data fetching, error handling, and transformation to the application's
 * expected CotisationSalariale structure.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

/**
 * Fetches all cotisations_salariales data from Supabase
 * 
 * @returns {Promise<CotisationSalarialeRow[]>} Array of raw cotisation data from Supabase
 * @throws {Error} If the fetch operation fails
 */
export async function fetchCotisationsSalariales(): Promise<CotisationSalarialeRow[]> {
  try {
    const { data, error } = await supabase
      .from('cotisations_salariales')
      .select('*')
      .order('id', { ascending: true });

    if (error) {
      console.error('Supabase error fetching cotisations_salariales:', error);
      throw new Error(`Failed to fetch cotisations data: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data returned from cotisations_salariales table');
    }

    console.log(`Successfully fetched ${data.length} cotisation records from Supabase`);
    return data;
  } catch (error) {
    console.error('Error in fetchCotisationsSalariales:', error);
    throw error;
  }
}

/**
 * Transforms raw Supabase data into CotisationSalariale structure
 * 
 * This function maps the database columns to the application's CotisationSalariale interface,
 * handling type conversions, default values, and converting percentages to decimals.
 * 
 * @param {CotisationSalarialeRow[]} rawData - Raw data from Supabase
 * @returns {CotisationSalariale[]} Transformed data ready for use in the application
 */
export function transformCotisationsSalariales(rawData: CotisationSalarialeRow[]): CotisationSalariale[] {
  return rawData
    .filter(row => row.cotisation_name && row.cotisation_name.trim() !== '') // Filter out invalid rows
    .map(row => {
      const cotisation: CotisationSalariale = {
        id: row.cotisation_name.toLowerCase().replace(/\s+/g, '_'), // Generate ID from name
        nom: row.cotisation_name,
        detail: row.cotisations_detail || '',
        assiette: row.assiette || '',
        tauxBas: Number(row.percentage_assiette_low) / 100 || 0, // Convert percentage to decimal
        tauxHaut: Number(row.percentage_assiette_high) / 100 || 0, // Convert percentage to decimal
        plafondTIBM: Number(row.plafond_TIBM) || 1, // Default to 1 (no ceiling)
        taux: Number(row.rate) / 100 || 0, // Convert percentage to decimal
        tauxNonDeductible: Number(row.rate_non_deductible) / 100 || 0 // Convert percentage to decimal
      };

      return cotisation;
    });
}

/**
 * Fetches and transforms cotisations_salariales data in one operation
 * 
 * This is a convenience function that combines fetching and transformation
 * into a single operation for easier use in React components.
 * 
 * @returns {Promise<CotisationSalariale[]>} Transformed cotisation data ready for use
 * @throws {Error} If fetch or transformation fails
 */
export async function getCotisationsSalariales(): Promise<CotisationSalariale[]> {
  try {
    const rawData = await fetchCotisationsSalariales();
    return transformCotisationsSalariales(rawData);
  } catch (error) {
    console.error('Error in getCotisationsSalariales:', error);
    throw error;
  }
}

/**
 * Validates that the fetched cotisation data is complete and consistent
 * 
 * @param {CotisationSalariale[]} cotisations - Transformed cotisation data to validate
 * @returns {boolean} True if data is valid, false otherwise
 */
export function validateCotisationData(cotisations: CotisationSalariale[]): boolean {
  if (!cotisations || cotisations.length === 0) {
    console.warn('No cotisations data available');
    return false;
  }

  // Check for required cotisations
  const requiredCotisations = ['cnracl', 'rafp', 'csg', 'crds'];
  const availableCotisationIds = cotisations.map(c => c.id);
  
  for (const requiredId of requiredCotisations) {
    const found = availableCotisationIds.some(id => id.includes(requiredId.toLowerCase()));
    if (!found) {
      console.warn(`Required cotisation containing "${requiredId}" is missing from data`);
      return false;
    }
  }

  // Validate cotisation structure
  for (const cotisation of cotisations) {
    if (!cotisation.id || !cotisation.nom || typeof cotisation.taux !== 'number') {
      console.warn(`Invalid cotisation structure for cotisation:`, cotisation);
      return false;
    }
    
    // Validate rates are reasonable (0-50%)
    if (cotisation.taux < 0 || cotisation.taux > 0.5) {
      console.warn(`Unreasonable cotisation rate for ${cotisation.nom}:`, cotisation.taux);
      return false;
    }
  }

  console.log('Cotisation data validation passed');
  return true;
}

/**
 * Default fallback cotisations data if Supabase is unavailable
 * 
 * This provides a basic set of cotisations with standard rates to ensure
 * the application continues to function even if Supabase is down.
 */
export const DEFAULT_COTISATIONS: CotisationSalariale[] = [
  {
    id: 'cnracl',
    nom: 'CNRACL',
    detail: 'Caisse Nationale de Retraites des Agents des Collectivités Locales - Cotisation retraite',
    assiette: 'Traitement indiciaire brut + primes',
    tauxBas: 0.1110, // 11.10%
    tauxHaut: 0.1110, // 11.10%
    plafondTIBM: 1, // Pas de plafond
    taux: 0.1110,
    tauxNonDeductible: 0
  },
  {
    id: 'rafp',
    nom: 'RAFP',
    detail: 'Retraite Additionnelle de la Fonction Publique - Cotisation retraite complémentaire',
    assiette: 'Primes et indemnités',
    tauxBas: 0.05, // 5%
    tauxHaut: 0.05, // 5%
    plafondTIBM: 0.2, // 20% du TIBM
    taux: 0.05,
    tauxNonDeductible: 0
  },
  {
    id: 'csg_deductible',
    nom: 'CSG déductible',
    detail: 'Contribution Sociale Généralisée déductible - Financement de la sécurité sociale',
    assiette: 'Revenus d\'activité',
    tauxBas: 0.068, // 6.8%
    tauxHaut: 0.068, // 6.8%
    plafondTIBM: 1, // Pas de plafond
    taux: 0.068,
    tauxNonDeductible: 0
  },
  {
    id: 'csg_non_deductible',
    nom: 'CSG non déductible',
    detail: 'Contribution Sociale Généralisée non déductible',
    assiette: 'Revenus d\'activité',
    tauxBas: 0.024, // 2.4%
    tauxHaut: 0.024, // 2.4%
    plafondTIBM: 1, // Pas de plafond
    taux: 0.024,
    tauxNonDeductible: 0.024
  },
  {
    id: 'crds',
    nom: 'CRDS',
    detail: 'Contribution au Remboursement de la Dette Sociale',
    assiette: 'Revenus d\'activité',
    tauxBas: 0.005, // 0.5%
    tauxHaut: 0.005, // 0.5%
    plafondTIBM: 1, // Pas de plafond
    taux: 0.005,
    tauxNonDeductible: 0.005
  },
  {
    id: 'contribution_de_solidarite',
    nom: 'Contribution de solidarité',
    detail: 'Contribution exceptionnelle de solidarité en faveur des travailleurs privés d\'emploi',
    assiette: 'Rémunération totale',
    tauxBas: 0.01, // 1%
    tauxHaut: 0.01, // 1%
    plafondTIBM: 1,
    taux: 0.01,
    tauxNonDeductible: 0
  }
];